import { useMemo, useState } from "react";
import { Button, Typography } from "antd";
import { CloseOutlined, ShareAltOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import OlitaskHeaderIcon from "components/olitask/OlitaskHeaderIcon";
import { useAuth } from "contexts/AuthContext";
import { useWorkspace } from "contexts/WorkspaceContext";
import paths from "routes/paths";
import "./WelcomeHomePage.css";

const { Text, Title } = Typography;

const firstNameOf = (user) => {
  const raw =
    user?.firstName ||
    user?.nickName ||
    user?.fullName ||
    user?.name ||
    user?.username ||
    "";
  return raw ? String(raw).trim().split(/\s+/)[0] : "";
};

const isHelperUse = (value) => String(value || "").toUpperCase() === "HELPER";

const helperStatusOf = (user) =>
  String(user?.helperStatus || user?.helperProfile?.helperStatus || "NONE").toUpperCase();

const hasHelperWorkspace = (workspaces = []) =>
  workspaces.some((workspace) => isHelperUse(workspace?.workspaceUse));

const popularIdeas = [
  {
    id: "airport-luggage",
    title: "Airport Luggage Help",
    subtitle: "Bags, airport pickup, drop-off, or curbside help",
    emoji: "🧳",
    kind: "TRAVO",
  },
  {
    id: "caretask",
    title: "Home Care & Assisted Living",
    subtitle: "Elderly care, special needs, home help, or nursing home visits",
    emoji: "❤️",
    kind: "CARE",
    organizationOnly: true,
  },
  {
    id: "house-cleaning",
    title: "House Cleaning",
    subtitle: "Vacuuming, walls, bathrooms, kitchen, and more",
    emoji: "🧽",
  },
  {
    id: "kitchen",
    title: "Kitchen reset",
    subtitle: "Dishes, counters, sweep & mop",
    emoji: "🍽️",
  },
  {
    id: "garage",
    title: "Organize garage",
    subtitle: "Sort bins and clear space",
    emoji: "🧰",
  },
  {
    id: "assembly",
    title: "Furniture assembly",
    subtitle: "Desk, shelf, or bed frame",
    emoji: "🪑",
  },
  {
    id: "laundry",
    title: "Laundry mountain",
    subtitle: "Fold, hang, put away",
    emoji: "👕",
  },
  {
    id: "yard",
    title: "Yard cleanup",
    subtitle: "Leaves, weeds, trim edges",
    emoji: "🍃",
  },
  {
    id: "other",
    title: "Other",
    subtitle: "Describe anything else you need help with",
    emoji: "✨",
  },
];

const hotCategories = [
  { id: "cleaning", title: "Home Cleaning", subtitle: "Quick tidy or deep clean", emoji: "🧼", hot: true },
  { id: "yard", title: "Yard Work", subtitle: "Mow, rake, trim", emoji: "🌿", hot: true },
  { id: "laundry", title: "Laundry", subtitle: "Wash, fold, organize", emoji: "🧺", hot: true },
  { id: "moving", title: "Moving Help", subtitle: "Load / unload items", emoji: "📦", hot: true },
  { id: "handyman", title: "Handyman", subtitle: "Small fixes & assembly", emoji: "🛠️" },
  { id: "errands", title: "Errands & Delivery", subtitle: "Pick up / drop off", emoji: "🛒" },
];

export default function WelcomeHomePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    workspaces,
    workspaceWorkMode,
    isOrganizationWorkspace,
    isHelperWorkspace,
  } = useWorkspace();
  const [showHelperPrompt, setShowHelperPrompt] = useState(true);
  const [showPendingNotice, setShowPendingNotice] = useState(true);

  const mode = workspaceWorkMode === "HELPER" || isHelperWorkspace ? "OFFER_HELP" : "GET_HELP";
  const firstName = firstNameOf(user);
  const helperStatus = helperStatusOf(user);
  const showHelperSignupCard =
    mode === "GET_HELP" &&
    helperStatus === "NONE" &&
    !hasHelperWorkspace(workspaces) &&
    showHelperPrompt;
  const showPendingHelperNotice =
    helperStatus === "PENDING" && showPendingNotice;
  const canRequestCareHelp = mode === "GET_HELP" && isOrganizationWorkspace;
  const visiblePopularIdeas = useMemo(
    () => popularIdeas.filter((idea) => !idea.organizationOnly || canRequestCareHelp),
    [canRequestCareHelp],
  );

  const openTaskCreate = (requestType) => {
    navigate(paths.taskCreateByType(requestType), { state: { requestType, source: "RequesterWelcome" } });
  };

  const shareOlitask = async () => {
    const shareUrl = `${window.location.origin}/download`;
    const shareText = "Join me on Olitask - get help, help others, or donate support in your community.";
    if (navigator.share) {
      try {
        await navigator.share({ title: "Share Olitask", text: shareText, url: shareUrl });
        return;
      } catch {
        return;
      }
    }
    await navigator.clipboard?.writeText(shareUrl);
  };

  return (
    <div className="welcome-home-page-shell">
      <div className="welcome-home-screen">
        <div className="welcome-home-glow welcome-home-glow-green" />
        <div className="welcome-home-glow welcome-home-glow-blue" />

        <div className="welcome-home-header-row">
          <div className="welcome-home-heading">
            <Title level={2} className="welcome-home-hi">
              {firstName ? `Welcome back, ${firstName}` : "Welcome back"} <span aria-hidden="true">👋</span>
            </Title>
            <Text className="welcome-home-sub">
              {mode === "GET_HELP"
                ? "You Need instant help NOW?"
                : "Ready to help someone nearby today?"}
            </Text>
          </div>

          <button
            type="button"
            className="welcome-home-task-shortcut"
            onClick={() => navigate(paths.myTasks)}
          >
            <span className="welcome-home-task-logo">
              <OlitaskHeaderIcon name={mode === "GET_HELP" ? "requester" : "helper"} size={28} />
            </span>
            <span>View Tasks</span>
          </button>
        </div>

        {showHelperSignupCard ? (
          <section className="welcome-home-helper-prompt">
            <button
              type="button"
              className="welcome-home-dismiss"
              onClick={() => setShowHelperPrompt(false)}
              aria-label="Dismiss helper signup prompt"
            >
              <CloseOutlined />
            </button>
            <span className="welcome-home-helper-pill">Become a Helper</span>
            <Title level={3}>Earn by helping people nearby</Title>
            <Text>
              Create a helper workspace to browse nearby requests, share your skills, and offer help
              when your schedule allows.
            </Text>
            <div className="welcome-home-helper-actions">
              <Button type="primary" onClick={() => navigate(paths.workspaceCreate())}>
                Sign Up Now
              </Button>
              <Button onClick={shareOlitask} icon={<ShareAltOutlined />}>
                Share With a Friend
              </Button>
              <Button type="text" onClick={() => setShowHelperPrompt(false)}>
                Remind Later
              </Button>
            </div>
          </section>
        ) : showPendingHelperNotice ? (
          <section className="welcome-home-pending-helper">
            <button
              type="button"
              className="welcome-home-dismiss"
              onClick={() => setShowPendingNotice(false)}
              aria-label="Dismiss helper review notice"
            >
              <CloseOutlined />
            </button>
            <span className="welcome-home-helper-pill">Helper Review</span>
            <Title level={3}>Your helper profile is under review</Title>
            <Text>Please wait for approval. We&apos;ll notify you once your helper account is ready.</Text>
          </section>
        ) : null}

        {mode === "GET_HELP" ? (
          <section className="welcome-home-cta welcome-home-cta-request">
            <Title level={3}>Post a request in under a minute</Title>
            <Text>Pick a category, add task details, submit - done.</Text>
            <div className="welcome-home-cta-buttons">
              <Button type="primary" size="large" onClick={() => openTaskCreate("GENERAL")}>
                Request General Help <span aria-hidden="true">💪⏰</span>
              </Button>
              <Button size="large" className="welcome-home-travo-btn" onClick={() => openTaskCreate("TRAVO")}>
                Airport Luggage <span aria-hidden="true">✈️ 🧳</span>
              </Button>
              {canRequestCareHelp ? (
                <Button size="large" className="welcome-home-care-btn" onClick={() => openTaskCreate("CARE")}>
                  Home Care & Assisted Living <span aria-hidden="true">❤️</span>
                </Button>
              ) : null}
            </div>
          </section>
        ) : (
          <section className="welcome-home-cta welcome-home-cta-helper">
            <Title level={3}>Help people in your area</Title>
            <Text>Browse help requests near you and jump in to help.</Text>
            <Button type="primary" size="large" onClick={() => navigate(paths.myTasks)}>
              Browse Nearby Help Requests <span aria-hidden="true">👀📍</span>
            </Button>
            {!isOrganizationWorkspace ? (
              <button
                type="button"
                className="welcome-home-organization-link"
                onClick={() => navigate(`${paths.workspaceCreate()}?accountType=ORGANIZATION`)}
              >
                Are you an organization? Sign up your organization to be able to offer Help
              </button>
            ) : null}
          </section>
        )}

        {mode === "GET_HELP" ? (
          <>
            <div className="welcome-home-section-header">
              <Title level={3}>What Do You Need Help With Today?</Title>
              <Text>Tap one below to quickly start your help request</Text>
            </div>
            <div className="welcome-home-idea-list">
              {visiblePopularIdeas.map((idea) => (
                <button
                  type="button"
                  key={idea.id}
                  className="welcome-home-row-card"
                  onClick={() => openTaskCreate(idea.kind || "GENERAL")}
                >
                  <span className="welcome-home-row-icon" aria-hidden="true">{idea.emoji}</span>
                  <span className="welcome-home-row-copy">
                    <strong>{idea.title}</strong>
                    <small>{idea.subtitle}</small>
                  </span>
                  <span className="welcome-home-row-chev">›</span>
                </button>
              ))}
            </div>
          </>
        ) : (
          <>
            <div className="welcome-home-section-header">
              <Title level={3}>Popular request categories</Title>
              <Text>These are the most common ways people ask for help</Text>
            </div>
            <div className="welcome-home-hot-grid">
              {hotCategories.map((category) => (
                <button
                  type="button"
                  key={category.id}
                  className="welcome-home-hot-card"
                  onClick={() => navigate(paths.myTasks)}
                >
                  <span className="welcome-home-hot-top">
                    <span aria-hidden="true">{category.emoji}</span>
                    {category.hot ? <span className="welcome-home-hot-pill">HOT</span> : null}
                  </span>
                  <strong>{category.title}</strong>
                  <small>{category.subtitle}</small>
                  <em>Browse nearby →</em>
                </button>
              ))}
            </div>
          </>
        )}

        <footer className="welcome-home-footer">
          Tip: Clear details get faster responses and better matches.
        </footer>
      </div>
    </div>
  );
}
